import type { RootState } from "@/store/types";

// Curated public projection of the host store. Deliberately minimal (design
// §3/§7): the churny slices — ryteData / yardMoves / yardDetectionData (YMS
// migration) — MUST NOT appear here. Fields land per real consumer.
// `hostAppId` is the seam probe that proves the plumbing end-to-end.
//
// `featureFlags` is a flat `{ key: enabled }` map — the first real consumer
// field (RelayYardTracker reads enableDriverVisitTab / enableYardDomainEventsTab
// to gate tabs). It's a generic map rather than named per-flag fields on
// purpose: `featureFlags` is a stable, non-churny host slice, so a map lets a
// new child read a new flag WITHOUT another 1-way HostPublicState change. Only
// the `enabled` boolean is projected — the slice's internal `{ enabled, fetching }`
// shape stays private.
export interface HostPublicState {
  readonly hostAppId: "ytyard360";
  readonly featureFlags: Readonly<Record<string, boolean>>;
  readonly auth: Readonly<{
    realm: string;
    domain: string;
    activeBuildingCode: string;
  }>;
}

const HOST_APP_ID = "ytyard360" as const;

interface FeatureFlagEntry {
  enabled?: boolean;
}

interface AuthInfoProjectionSource {
  realm?: string;
  domain?: string;
  activeBuildingCode?: string;
}

const toFlagMap = (
  flags: Record<string, FeatureFlagEntry> | undefined
): Readonly<Record<string, boolean>> => {
  const map: Record<string, boolean> = {};
  if (flags) {
    for (const key of Object.keys(flags)) {
      map[key] = flags[key]?.enabled ?? false;
    }
  }
  return Object.freeze(map);
};

// Memoize on state reference so `getSnapshot` in useHostSelector returns a
// stable snapshot ref when Redux returns a stable state ref (combineReducers
// early-out for actions no slice handles). React 18's useSyncExternalStore
// contract requires getSnapshot returns the same value when nothing changed.
// WeakMap-keyed so the cache never pins a superseded RootState tree: a
// module-level `let lastState` would strongly retain the old tree until the
// next call, which may never come once all federation consumers unmount.
const projectionCache = new WeakMap<RootState, HostPublicState>();

export const toHostPublicState = (state: RootState): HostPublicState => {
  const cached = projectionCache.get(state);
  if (cached) return cached;
  // featureFlags is a JS (untyped) reducer, so RootState infers it as `{}`.
  // Narrow through `unknown` to the runtime `{ key: { enabled } }` shape.
  const projection: HostPublicState = Object.freeze({
    hostAppId: HOST_APP_ID,
    featureFlags: toFlagMap(
      state.featureFlags as unknown as Record<string, FeatureFlagEntry> | undefined
    ),
    auth: Object.freeze({
      realm: (state.authInfo as AuthInfoProjectionSource | undefined)?.realm ?? "",
      domain: (state.authInfo as AuthInfoProjectionSource | undefined)?.domain ?? "",
      activeBuildingCode:
        (state.authInfo as AuthInfoProjectionSource | undefined)?.activeBuildingCode ?? "",
    }),
  });
  projectionCache.set(state, projection);
  return projection;
};
